// ────────────────────────────────────────────────────────────────────
// LoginScreen — 1:1 with design_handoff_one_platform_signin/README.md
//
// Layout: 50/50 split
//   Left  · navy gradient + refinery silhouette + UTC clock + tagline
//   Right · cream paper + dot grid + form (eyebrow / h2 / sub / fields
//           / SIGN IN button)
//
// No logo per customer request.
//
// All form helpers (Field) + icons inline below to
// keep the file self-contained. Lift to /components/ later if reused.
// ────────────────────────────────────────────────────────────────────

import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useLogin, errMsg } from "@/lib/auth";
import { apiClient, SESSION_EXPIRED_KEY } from "@/lib/api";

// Read at 13 sites on the sign-in card. It was the old accent while the top
// bar beside it already read var(--accent), so the card showed two oranges.
const ACCENT = "var(--accent)";
// Accent as TEXT, per the 2026-08-31 ruling: fills keep --accent, small text
// takes --accent-deep. Measured on production 2026-09-01, --accent as text on
// this card is 3.90:1 against a 4.5 requirement. Round 6 converted 99 such
// sites and missed these, because the colour reaches `color:` through this
// constant rather than as a literal var(--accent) -- the same shape as the
// SubmissionsScreen miss. The wordmark E stays ACCENT: 20px bold clears
// AA-large, and it is identity rather than chrome.
const ACCENT_TEXT = "var(--accent-deep)";

// Build identity — shown in the footer for engineering-grade credibility.
const APP_VERSION = "1.0.0";
const BUILD_ENV = (import.meta.env.MODE || "production").toLowerCase();
const IS_PROD = BUILD_ENV === "production";
const SUPPORT_EMAIL = "support@xzymods.com";
const REMEMBER_KEY = "xzynexa.remember_user";

type LoginError = { msg: string; kind: "error" | "warn" };

/** Classify a failed login into a user-facing message + severity. The backend
 *  already differentiates 401 (bad creds) / 403 (deactivated) / 429 (throttled);
 *  a missing response means the network/server is unreachable. */
function classifyLoginError(err: unknown): LoginError {
  if (axios.isAxiosError(err)) {
    if (!err.response) {
      return { msg: "Cannot reach the server — check your connection and try again.", kind: "error" };
    }
    const status = err.response.status;
    if (status === 429) {
      return { msg: errMsg(err) || "Too many failed attempts — wait a few minutes before retrying.", kind: "warn" };
    }
    if (status === 403) {
      return { msg: errMsg(err) || "This account is deactivated. Contact your administrator.", kind: "error" };
    }
    if (status >= 500) {
      // 5xx (incl. a dev-proxy failure when the backend is down) — don't echo
      // the raw axios string; the user can't act on "status code 500".
      return { msg: "The server is temporarily unavailable. Please try again in a moment.", kind: "error" };
    }
  }
  return { msg: errMsg(err) || "Invalid username or password.", kind: "error" };
}

// ─────────────────────────────────────────────────────────────────────
// UTC clock — updates every 1s
// ─────────────────────────────────────────────────────────────────────
function useUtcClock(): { date: string; time: string } {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())}`,
    time: `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}`,
  };
}

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────
export default function LoginScreen(): JSX.Element {
  // Remembered username survives logout; dev convenience prefill only in dev.
  const rememberedUser = (() => {
    try { return localStorage.getItem(REMEMBER_KEY) || ""; } catch { return ""; }
  })();
  const [username, setUsername] = useState(rememberedUser || (import.meta.env.DEV ? "admin" : ""));
  const [password, setPassword] = useState(import.meta.env.DEV ? "admin123" : "");
  const [remember, setRemember] = useState(!!rememberedUser);
  const [showPwd, setShowPwd]   = useState(false);
  const [error, setError]       = useState<LoginError | null>(null);
  const [errNonce, setErrNonce] = useState(0);   // bump → replay the shake

  // Show "your session expired" only if an authenticated request was rejected
  // (flag set by the api 401 interceptor). Read once, then clear.
  const [sessionExpired] = useState<boolean>(() => {
    try {
      if (sessionStorage.getItem(SESSION_EXPIRED_KEY)) {
        sessionStorage.removeItem(SESSION_EXPIRED_KEY);
        return true;
      }
    } catch { /* ignore */ }
    return false;
  });

  const navigate = useNavigate();
  const [search] = useSearchParams();
  const next     = search.get("next") || "/";
  const login    = useLogin();

  // must_change_password flow (handled here — the single login surface)
  const [mustChange, setMustChange] = useState(false);

  /** Single front door: everyone lands on the empty desk ("/"). Atlas and the
   *  staff studios are ordinary destinations reached from the menu/hand-off,
   *  so admins are NOT locked out of any portal view. A `?next=` deep link
   *  (set by the auth 401 interceptor) still wins over this default. */
  function routeAfter(): void {
    navigate(next, { replace: true });
  }

  function persistRemember(): void {
    try {
      if (remember) localStorage.setItem(REMEMBER_KEY, username);
      else localStorage.removeItem(REMEMBER_KEY);
    } catch { /* ignore */ }
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      const data = await login.mutateAsync({ username, password });
      persistRemember();
      if (data.must_change_password) {
        setMustChange(true);   // show set-new-password step
        return;
      }
      setTimeout(routeAfter, 700);   // theatre delay
    } catch (err) {
      setError(classifyLoginError(err));
      setErrNonce((n) => n + 1);
    }
  }
  const loading = login.isPending;
  const success = login.isSuccess && !mustChange;   // brief "access granted" beat
  const clock = useUtcClock();

  // Forced password change (first login). The current password is still in
  // `password` state, so we can satisfy the backend without re-asking for it.
  if (mustChange) {
    return <ChangePasswordScreen currentPassword={password} onDone={routeAfter} />;
  }

  // Centered sign-in card over a softened, full-bleed refinery backdrop.
  return (
    <div style={{ minHeight: "100vh", position: "relative", overflow: "hidden", background: "#06121F", display: "flex", flexDirection: "column" }}>
      {/* full-bleed background image */}
      <div aria-hidden style={{ position: "absolute", inset: 0, backgroundImage: "url(/login-bg.webp)", backgroundSize: "cover", backgroundPosition: "center" }} />
      {/* cinematic darken — vertical gradient + vignette so the card stays the focus */}
      <div aria-hidden style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(4,10,20,.58) 0%, rgba(4,10,20,.24) 36%, rgba(4,10,20,.46) 66%, rgba(3,8,16,.88) 100%)" }} />
      <div aria-hidden style={{ position: "absolute", inset: 0, background: "radial-gradient(78% 68% at 50% 46%, rgba(0,0,0,0) 40%, rgba(3,8,16,.55))" }} />

      {/* slim telemetry strip (engineering credibility) */}
      <div className="relative font-mono uppercase" style={{ height: 46, padding: "0 28px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11, letterSpacing: ".2em", color: "rgba(232,238,247,.5)" }}>
        <div className="flex items-center gap-3"><Pulse color="#3DC97B" /><span>LIVE TELEMETRY</span><span className="hidden sm:inline opacity-40">—</span><span className="hidden sm:inline" style={{ color: "rgba(232,238,247,.34)" }}>SECURE · TLS V1.3</span>{!IS_PROD && (
          <span style={{ marginLeft: 4, padding: "2px 8px", fontSize: 9.5, letterSpacing: ".22em", color: "#06121F", background: "var(--accent-2)", fontWeight: 700, borderRadius: 2 }}>
            {BUILD_ENV.toUpperCase()}
          </span>
        )}</div>
        <div className="flex items-center gap-2.5"><span className="hidden sm:inline" style={{ color: "rgba(232,238,247,.4)" }}>{clock.date}</span><span className="tabular-nums" style={{ color: "#E8EEF7", letterSpacing: ".14em" }}>{clock.time}</span><span className="opacity-40">UTC</span></div>
      </div>

      {/* centered card */}
      <div className="relative" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div className="auth-card-in" style={{ width: "min(432px, 100%)" }}>
          <div style={{ position: "relative", background: "rgba(247,245,239,.94)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,.16)", borderRadius: 14, boxShadow: "inset 0 1px 0 rgba(255,255,255,.55), 0 32px 80px -14px rgba(0,0,0,.72), 0 10px 28px -10px rgba(0,0,0,.5)", overflow: "hidden" }}>
            {/* accent top bar */}
            <div aria-hidden style={{ height: 3, background: "linear-gradient(90deg, transparent, var(--accent) 28%, var(--accent-2) 50%, var(--accent) 72%, transparent)" }} />
            {/* faint engineering dot grid (design-handoff motif); white fields sit above it */}
            <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none",
                 backgroundImage: "radial-gradient(rgba(10,22,40,.045) 1px, transparent 1px)",
                 backgroundSize: "18px 18px" }} />
            <div style={{ position: "relative", padding: "26px 36px 30px" }}>
              {/* letterhead: wordmark + platform tag */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 18, marginBottom: 22, borderBottom: "1px solid var(--hairline)" }}>
                <div className="font-sans" style={{ fontWeight: 800, fontSize: 22, letterSpacing: "-.02em", color: "var(--ink-1)", lineHeight: 1 }}>
                  XZYN<span style={{ color: ACCENT }}>E</span>XA
                </div>
                <div className="font-mono uppercase" style={{ fontSize: 8.5, lineHeight: 1.5, letterSpacing: ".2em", color: "var(--ink-4)", textAlign: "right" }}>
                  Asset Integrity<br />Platform
                </div>
              </div>

              <CardForm
                username={username} setUsername={setUsername}
                password={password} setPassword={setPassword}
                showPwd={showPwd} setShowPwd={setShowPwd}
                remember={remember} setRemember={setRemember}
                loading={loading} success={success} error={error} errNonce={errNonce}
                sessionExpired={sessionExpired}
                onSubmit={onSubmit}
              />
            </div>
          </div>

          <div className="font-mono uppercase" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, fontSize: 9.5, letterSpacing: ".2em", color: "rgba(232,238,247,.5)" }}>
            <span>XZYMODS</span>
            <span style={{ display: "flex", alignItems: "center", gap: 7 }}><LockIcon /> Authorized access only</span>
            <span>v{APP_VERSION} · © {new Date().getFullYear()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// CHANGE PASSWORD — forced on first sign-in (must_change_password)
// ─────────────────────────────────────────────────────────────────────
function ChangePasswordScreen({ currentPassword, onDone }: {
  currentPassword: string; onDone: () => void;
}): JSX.Element {
  const [pw, setPw]   = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    if (pw.length < 6)          { setErr("New password must be at least 6 characters"); return; }
    if (pw !== pw2)             { setErr("Passwords do not match"); return; }
    if (pw === currentPassword) { setErr("New password must differ from the current one"); return; }
    setBusy(true);
    try {
      await apiClient.changePassword(currentPassword, pw);
      onDone();
    } catch (e2) {
      setErr(errMsg(e2));
      setBusy(false);
    }
  }

  const field = "border rounded px-3 py-2.5 font-sans";
  const fieldStyle = { borderColor: "var(--hairline)", fontSize: 15 } as const;
  return (
    <div className="min-h-screen flex items-center justify-center"
         style={{ background: "var(--paper)", color: "var(--ink-1)" }}>
      <form onSubmit={submit} className="flex flex-col gap-4" style={{ width: "min(420px, 90%)" }}>
        <div className="font-mono uppercase"
             style={{ fontSize: 10.5, letterSpacing: ".24em", color: ACCENT_TEXT }}>
          / Set a new password
        </div>
        <h2 className="m-0 font-sans font-medium" style={{ fontSize: 28, letterSpacing: "-.02em" }}>
          Change your password
        </h2>
        <p className="m-0 font-sans" style={{ fontSize: 14, color: "var(--ink-3)" }}>
          First sign-in requires setting a new password.
        </p>
        <input type="password" placeholder="New password" value={pw} autoFocus
               onChange={(e) => setPw(e.target.value)} className={field} style={fieldStyle} />
        <input type="password" placeholder="Confirm new password" value={pw2}
               onChange={(e) => setPw2(e.target.value)} className={field} style={fieldStyle} />
        {err && <div style={{ color: "var(--err)", fontSize: 13 }}>{err}</div>}
        <button type="submit" disabled={busy}
                className="py-2.5 rounded font-medium text-white"
                style={{ background: ACCENT, opacity: busy ? 0.6 : 1 }}>
          {busy ? "Saving…" : "Set password & continue"}
        </button>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// CARD FORM — sign-in fields inside the centered card
// ─────────────────────────────────────────────────────────────────────
interface CardFormProps {
  username: string; setUsername: (v: string) => void;
  password: string; setPassword: (v: string) => void;
  showPwd: boolean; setShowPwd: (v: boolean) => void;
  remember: boolean; setRemember: (v: boolean) => void;
  loading: boolean; success: boolean; error: LoginError | null; errNonce: number;
  sessionExpired: boolean;
  onSubmit: (e: FormEvent) => void;
}

function CardForm(props: CardFormProps): JSX.Element {
  const [caps, setCaps] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  // Caps Lock is one of the top causes of failed sign-ins — detect on the pw field.
  function probeCaps(e: React.KeyboardEvent): void {
    try { setCaps(e.getModifierState?.("CapsLock") ?? false); } catch { /* ignore */ }
  }
  return (
    <>
      <div className="flex items-center gap-2.5 mb-3 font-mono uppercase"
           style={{ fontSize: 10, letterSpacing: ".24em", color: ACCENT_TEXT }}>
        <span style={{ width: 20, height: 1, background: ACCENT }} />
        <span>Authentication Required</span>
      </div>
      <h2 className="m-0 font-sans font-medium"
          style={{ fontSize: 28, letterSpacing: "-.02em", color: "var(--ink-1)", lineHeight: 1.1 }}>
        Sign in to your portal
      </h2>
      <p className="font-sans"
         style={{ marginTop: 8, marginBottom: 26, fontSize: 13.5, lineHeight: 1.55, color: "var(--ink-3)" }}>
        Inspection data for your assigned units — every engineering value independently
        verified, graded for confidence, and traceable to its source.
      </p>

      {props.sessionExpired && !props.error && (
        <div className="auth-banner-in mb-4 px-3 py-2 font-mono" role="status"
             style={{ fontSize: 12, color: "var(--warn)", background: "rgba(var(--accent-rgb) / .1)",
                      border: "1px solid rgba(var(--accent-rgb) / .4)", borderRadius: 7 }}>
          Your session expired. Please sign in again.
        </div>
      )}

      <form onSubmit={props.onSubmit} autoComplete="off">
        <Field
          label="USERNAME"
          glyph="@"
          input={
            <input type="text" value={props.username}
                   onChange={(e) => props.setUsername(e.target.value)}
                   autoFocus={!props.username}
                   style={inputStyle()} autoComplete="username" />
          }
        />
        <Field
          label="PASSWORD"
          glyph={<KeyIcon />}
          aux={
            <button type="button" onClick={() => setShowForgot((v) => !v)} className="font-mono uppercase"
               style={{ fontSize: 10, letterSpacing: ".2em", color: "var(--ink-3)", background: "transparent",
                        cursor: "pointer", textDecoration: "none", borderBottom: "1px solid var(--hairline)", padding: "0 0 1px" }}>
              FORGOT?
            </button>
          }
          input={
            <div className="flex items-center flex-1">
              <input type={props.showPwd ? "text" : "password"} value={props.password}
                     onChange={(e) => props.setPassword(e.target.value)}
                     onKeyUp={probeCaps} onKeyDown={probeCaps}
                     autoFocus={!!props.username}
                     style={inputStyle()} autoComplete="current-password" />
              <button type="button" onClick={() => props.setShowPwd(!props.showPwd)}
                      aria-label={props.showPwd ? "Hide password" : "Show password"} aria-pressed={props.showPwd}
                      className="transition-colors hover:!text-[var(--ink-1)]"
                      style={{ background: "transparent", border: 0, color: "var(--ink-3)",
                               width: 34, height: 34, cursor: "pointer", padding: 0, marginRight: -6,
                               display: "flex", alignItems: "center", justifyContent: "center" }}>
                <EyeIcon open={props.showPwd} />
              </button>
            </div>
          }
        />

        {caps && (
          <div className="auth-banner-in mb-3 font-mono uppercase flex items-center gap-2"
               style={{ marginTop: -8, fontSize: 10, letterSpacing: ".14em", color: "#B07A12" }}>
            <CapsIcon /> Caps Lock is on
          </div>
        )}

        {showForgot && (
          <div className="auth-banner-in mb-3 px-3 py-2.5 font-sans"
               style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--ink-2)",
                        background: "rgba(10,22,40,.04)", border: "1px solid var(--hairline)",
                        borderRadius: 7 }}>
            Password resets are handled by your administrator. Contact{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: ACCENT_TEXT, textDecoration: "none", fontWeight: 600 }}>
              {SUPPORT_EMAIL}
            </a>{" "}
            and you'll receive a one-time temporary password to sign in with.
          </div>
        )}

        {props.error && (
          <div key={props.errNonce} className="auth-banner-in auth-shake mb-3 px-3 py-2 font-mono" role="alert"
               style={props.error.kind === "warn"
                 ? { fontSize: 12, color: "var(--warn)", background: "rgba(var(--accent-rgb) / .1)", border: "1px solid rgba(var(--accent-rgb) / .45)", borderRadius: 7 }
                 : { fontSize: 12, color: "var(--err)", background: "color-mix(in srgb, var(--err) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--err) 40%, transparent)", borderRadius: 7 }}>
            {props.error.msg}
          </div>
        )}

        <label className="flex items-center gap-2.5 mb-1 font-mono uppercase select-none"
               style={{ fontSize: 10, letterSpacing: ".16em", color: "var(--ink-3)", cursor: "pointer" }}>
          <input type="checkbox" checked={props.remember}
                 onChange={(e) => props.setRemember(e.target.checked)}
                 style={{ width: 14, height: 14, accentColor: ACCENT, cursor: "pointer" }} />
          Remember my username
        </label>

        <button type="submit" disabled={props.loading || props.success} className="auth-cta w-full font-mono"
                style={{ marginTop: 10, height: 52,
                         background: props.success ? "#3DC97B" : ACCENT, color: "var(--ink-1)",
                         border: 0, borderRadius: 9, fontSize: 12, fontWeight: 600, letterSpacing: ".26em",
                         cursor: props.loading ? "wait" : props.success ? "default" : "pointer",
                         display: "flex", alignItems: "center", justifyContent: "center", gap: 14,
                         transition: "transform .15s, filter .15s, background .25s, box-shadow .2s" }}
                onMouseDown={(e) => !props.success && (e.currentTarget.style.transform = "translateY(1px)")}
                onMouseUp={(e) => (e.currentTarget.style.transform = "")}
                onMouseEnter={(e) => !props.loading && !props.success && (e.currentTarget.style.boxShadow = "0 8px 24px -6px rgba(var(--accent-rgb) / .55)")}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}>
          <span>{props.success ? "ACCESS GRANTED" : props.loading ? "AUTHENTICATING" : "SIGN IN"}</span>
          <span className="auth-cta-icon">{props.success ? <CheckIcon /> : props.loading ? <Spinner /> : <ArrowIcon />}</span>
        </button>

      </form>

      <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--hairline)" }}>
        <div className="font-sans" style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
          Need access to your assigned units?{" "}
          <a href={`mailto:${SUPPORT_EMAIL}?subject=Portal%20access%20request`}
             style={{ color: ACCENT_TEXT, textDecoration: "none", fontWeight: 600 }}>
            Request from your administrator
          </a>
        </div>
        <div className="font-mono uppercase flex items-center gap-2"
             style={{ marginTop: 12, fontSize: 9, lineHeight: 1.6, letterSpacing: ".13em", color: "var(--ink-4)" }}>
          <span style={{ flexShrink: 0, display: "inline-flex" }}><LockIcon /></span>
          <span>Restricted system · access is logged &amp; monitored · unauthorized use prohibited</span>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Form helpers
// ─────────────────────────────────────────────────────────────────────
function inputStyle(): React.CSSProperties {
  return {
    flex: 1, height: 36,
    background: "transparent", border: 0, outline: "none",
    fontFamily: "var(--mono)", fontSize: 15,
    color: "var(--ink-1)", letterSpacing: ".02em",
    padding: 0, minWidth: 0,
  };
}

function Field({
  label, glyph, input, aux,
}: {
  label: string;
  glyph: ReactNode;
  input: ReactNode;
  aux?: ReactNode;
}): JSX.Element {
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  return (
    <div className="mb-[18px]" onFocusCapture={() => setFocused(true)} onBlurCapture={() => setFocused(false)}
         onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div className="flex justify-between items-baseline mb-2">
        <label className="font-mono uppercase flex items-center gap-2.5"
               style={{
                 fontSize: 10.5, letterSpacing: ".22em",
                 color: focused ? ACCENT : "var(--ink-3)",
                 transition: "color .15s",
               }}>
          <span style={{
            display: "inline-flex", width: 12, justifyContent: "center",
            color: focused ? ACCENT : "var(--ink-4)", transition: "color .15s",
          }}>
            {glyph}
          </span>
          {label}
        </label>
        {aux}
      </div>
      <div className="relative flex items-center"
           style={{
             padding: "0 14px", height: 50,
             background: "var(--paper-2)",
             border: `1px solid ${focused ? "color-mix(in srgb, var(--ink-1) 40%, transparent)" : hovered ? "color-mix(in srgb, var(--ink-1) 26%, transparent)" : "var(--hairline)"}`,
             borderRadius: 9,
             overflow: "hidden",   // clip the focus underline to the radius
             transition: "border-color .15s",
           }}>
        {input}
        {/* Focus accent underline */}
        <span style={{
          position: "absolute", left: 0, right: 0, bottom: -1, height: 2,
          background: ACCENT, transformOrigin: "left center",
          transform: `scaleX(${focused ? 1 : 0})`,
          transition: "transform .25s cubic-bezier(.6,.2,.2,1)",
        }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Icons (all inline · no external lib)
// ─────────────────────────────────────────────────────────────────────
function Pulse({ color }: { color: string }): JSX.Element {
  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      <span
        className="animate-pulse-dot"
        style={{
          width: 8, height: 8, borderRadius: 9, background: color,
          boxShadow: `0 0 0 0 ${color}66`,
        }}
      />
    </span>
  );
}

function LockIcon(): JSX.Element {
  return (
    <svg width="11" height="12" viewBox="0 0 24 26" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="11" width="16" height="13" rx="1" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}
function KeyIcon(): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="8" cy="12" r="4" />
      <path d="M12 12h10v4M18 12v4" />
    </svg>
  );
}
function EyeIcon({ open }: { open: boolean }): JSX.Element {
  return open ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 12s4-7 10-7c2.5 0 4.7 1.2 6.5 2.7M22 12s-4 7-10 7c-2.5 0-4.7-1.2-6.5-2.7" />
      <path d="M3 3l18 18" />
    </svg>
  );
}
function CapsIcon(): JSX.Element {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
      <path d="M12 4 4 12h4v4h8v-4h4z" />
      <rect x="8" y="18" width="8" height="2" />
    </svg>
  );
}
function ArrowIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}
function CheckIcon(): JSX.Element {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
function Spinner(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24"
         style={{ animation: "spin 1s linear infinite" }}>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity=".2" strokeWidth="2" />
      <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
